import moment from 'moment'
import type {Moment} from 'moment'
import {range, zipWith} from 'lodash'

export const ONE_PAGE_ROWS = 16
export const TWO_PAGE_ROWS = 31

interface RowData {
  rowNumber: number
  insuredTermStartOn: string | null
  insuredTermEndOn: string | null
  insuredBaseDateNum: number | null
  paymentTermStartOn: string | null
  paymentTermEndOn: string | null
  paymentBaseDateNum: number | null
}

export const getPaymentDayAtMonth = (monthDate: Moment, closingDate: number) => {
  if (closingDate == -1) {
    return monthDate.clone().add(1, 'month').startOf('month').subtract(1, 'day')
  }
  const date = monthDate.clone()
  date.set('date', closingDate)
  if (date.get('date') == closingDate) {
    return date
  }
  return date.startOf('month').subtract(1, 'day')
}

export const getLastPaymentDay = (date: Moment, closingDate: number) => {
  const atMonth = getPaymentDayAtMonth(date, closingDate)
  return atMonth.isSameOrAfter(date, 'day') ? getPaymentDayAtMonth(atMonth.subtract(1, 'month'), closingDate) : atMonth
}

const dateFormat = (date: Moment) => date.format('YYYY/MM/DD')

export const makeRowsData = (
  startedOn: string | null | undefined,
  joinedAt: string,
  salaryClosingDate: number,
  maxIndex = ONE_PAGE_ROWS
): RowData[] => {
  if (startedOn === undefined || startedOn === null) {
    return []
  }
  const paymentData = [...makePaymentData(startedOn, joinedAt, salaryClosingDate, maxIndex)]
  const insuranceData = [...makeInsuranceData(startedOn, joinedAt, maxIndex)]
  return zipWith(
    range(Math.max(insuranceData.length, paymentData.length)).map((no) => ({
      rowNumber: no,
      insuredTermEndOn: null,
      insuredTermStartOn: null,
      insuredBaseDateNum: null,
      paymentTermStartOn: null,
      paymentTermEndOn: null,
      paymentBaseDateNum: null,
    })),
    insuranceData,
    paymentData,
    Object.assign
  )
}

export const makeInsuranceData = function* (startedOn: string, joinedAt: string, maxIndex: number) {
  const mStartedOn = moment(startedOn).subtract(1, 'day')
  const mJoinedAt = moment(joinedAt)
  const termBaseDate = mStartedOn.clone().add(1, 'day')
  let index = 0
  while (index < maxIndex) {
    let insuranceStart = termBaseDate.clone().subtract(1 + index, 'month')
    const insuranceEnd = termBaseDate.clone().subtract(index, 'month').subtract(1, 'day')
    if (insuranceStart.isSameOrBefore(mJoinedAt, 'day')) {
      insuranceStart = mJoinedAt
    }
    yield {
      insuredTermStartOn: dateFormat(insuranceStart),
      insuredTermEndOn: dateFormat(insuranceEnd),
      insuredBaseDateNum: insuranceEnd.diff(insuranceStart, 'days') + 1,
    }
    if (insuranceStart.isSame(mJoinedAt, 'day')) {
      return
    }
    index++
  }
}

export const makePaymentData = function* (startedOn: string, joinedAt: string, closingDate: number, maxIndex: number) {
  const mStartedOn = moment(startedOn).subtract(1, 'day')
  const mJoinedAt = moment(joinedAt)
  let paymentStart = getLastPaymentDay(mStartedOn, closingDate).add(1, 'day')
  let paymentEnd = mStartedOn
  let index = 0
  while (index < maxIndex) {
    if (paymentStart.isSameOrBefore(mJoinedAt, 'day')) {
      paymentStart = mJoinedAt
    }
    yield {
      paymentTermStartOn: dateFormat(paymentStart),
      paymentTermEndOn: dateFormat(paymentEnd),
      paymentBaseDateNum: paymentEnd.diff(paymentStart, 'days') + 1,
    }
    if (paymentStart.isSame(mJoinedAt, 'day')) {
      return
    }
    index++
    paymentEnd = paymentStart.clone().subtract(1, 'day')
    paymentStart = getLastPaymentDay(paymentEnd, closingDate).clone().add(1, 'day')
  }
}

export const workingYearAndMonths = (joinedAt: string, leavedAt: string | undefined): [number, number] => {
  const base = (leavedAt ? moment(leavedAt) : moment()).add(1, 'day')
  const months = base.diff(moment(joinedAt), 'months')
  return [Math.floor(months / 12), months % 12]
}
